import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/_components/dialog";
import { Button } from "@/components/_components/button";
import { Download } from "lucide-react";
import { useMemo } from "react";

interface JsonViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  data: Record<string, unknown>;
}

export function JsonViewerDialog({ open, onOpenChange, title, data }: JsonViewerDialogProps) {
  const jsonString = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return "// Unable to serialize data (possible circular reference)";
    }
  }, [data]);

  const handleDownload = () => {
    try {
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${title.replace(/\s+/g, "_").toLowerCase()}_${new Date().getTime()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download JSON:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[75vw] max-h-[75vh] w-[75vw]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{title}</DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="h-8"
            >
              <Download className="h-4 w-4 mr-2" />
              Download JSON
            </Button>
          </div>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[60vh]">
          <pre className="text-sm text-foreground whitespace-pre-wrap font-mono bg-muted p-4 rounded-lg border border-border">
            {jsonString}
          </pre>
        </div>
      </DialogContent>
    </Dialog>
  );
}
